import { DOCUMENT, Inject, Injectable, inject } from '@angular/core';
import { CountryToken, EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import { Meta, Title } from '@angular/platform-browser';
import { buildConfig } from '@app/config/config';
import { buildResources } from '@app/config/resources';
import { Vertical } from '@models/vertical.model';
import { PriceGuide } from '@models/guides/priceGuide.model';
import { Router } from '@angular/router';
import { TranslateService } from '@services/logic/translate.service';
import { toAbsoluteUrl } from '@utils/urlHelpers';

@Injectable({
  providedIn: 'root',
})
export class SEOService {
  protected readonly env = inject(EnvToken);
  protected readonly seg = inject(SeoSegmentsToken);
  private readonly country = inject(CountryToken);
  private readonly config = buildConfig(this.env, this.country);
  private readonly RES = buildResources(this.env);

  private _jsonSnippet?: HTMLScriptElement;
  private _graphObjects: any[] = [];

  private _canonicalLink?: HTMLLinkElement;

  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private doc: Document,
    private router: Router,
    private translateService: TranslateService,
  ) {
    this.AddTags();
  }

  AddTags() {
    // get fixed tags and add, always check since we have inherited services
    // @ts-ignore / TODO:// mark
    this.meta.addTags(this.config.SEO.tags);

    // add canonical link
    const _canonical = this.doc.querySelector('link[rel="canonical"]');
    this._canonicalLink =
      (_canonical as HTMLLinkElement) || this.createCanonicalLink();

    this._jsonSnippet =
      this.doc.querySelector<HTMLScriptElement>(
        'script[data-hh-seo-jsonld="true"]',
      ) ?? undefined;
  }
  get url(): string {
    let url = this.doc.location.pathname;
    if (url.indexOf(';') > -1) {
      url = url.substring(0, url.indexOf(';'));
    }
    return url;
  }

  /**** Private functions  ****/
  /**
   * Collapse repeated slashes in the path of a canonical URL.
   *
   * Pages compose their canonical as `${domain}${path}`. When `path` already
   * carries a leading `/` the naive template yields
   * `https://handyhand.dk//index/pris-data`, which Google treats as a
   * different document than the URL it crawled. Normalizing here — at the
   * single sink that writes the tag — means no individual page can
   * reintroduce it.
   */
  private collapseSlashes(url: string): string {
    const schemeEnd = url.indexOf('://');
    if (schemeEnd === -1) {
      return url.replace(/\/{2,}/g, '/');
    }
    const scheme = url.slice(0, schemeEnd + 3);
    return scheme + url.slice(schemeEnd + 3).replace(/\/{2,}/g, '/');
  }

  private createCanonicalLink(): HTMLLinkElement {
    // append canonical to body
    const _canonicalLink = this.doc.createElement('link');
    _canonicalLink.setAttribute('rel', 'canonical');
    this.doc.head.appendChild(_canonicalLink);

    return _canonicalLink;
  }

  private createJsonSnippet(): HTMLScriptElement {
    // if on browser platform, return

    const _script = this.doc.createElement('script');
    // set attribute to application/ld+json
    _script.setAttribute('type', 'application/ld+json');
    _script.setAttribute('data-hh-seo-jsonld', 'true');

    // append and return reference
    this.doc.body.appendChild(_script);
    return _script;
  }

  private getJsonSnippet(): HTMLScriptElement {
    if (!this._jsonSnippet) {
      this._jsonSnippet = this.createJsonSnippet();
    }

    return this._jsonSnippet;
  }

  /*** Protected functions ***/
  protected setUrl(url?: string, params?: any) {
    // params: type = IListParams
    if (!url) {
      // A page that passes no canonical must point at itself. There is no
      // meaningful site-wide default: any other URL tells Google this page is
      // a duplicate of a document it isn't. Router.url is correct on both
      // platforms (SSR included); strip query/matrix/fragment so tracking
      // params can't fork the canonical.
      const [routedPath] = this.router.url.split(/[?;#]/);
      url = toAbsoluteUrl(this.env.domain, routedPath || '/');
    }

    if (url.indexOf(';') > -1) {
      url = url.substring(0, url.indexOf(';'));
    }

    url = this.collapseSlashes(url);

    // if category or page exist, append them as query params
    // or matrix params, that should be okay, but not wise
    if (params) {
      const s = new URLSearchParams();
      params.category && s.append('category', params.category.key);
      params.page && s.append('page', params?.page?.toString());
      url += '?' + s?.toString();
    }

    // set attribute and og:url

    this._canonicalLink?.setAttribute('href', url);
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
  }

  /**
   * @param appendSiteName When false, `title` is used verbatim. Pages whose
   * stored title tag is already the complete `<title>` (DK price guides) opt
   * out so the site name is not appended twice.
   */
  protected setTitle(title: string, appendSiteName = true) {
    const _title = appendSiteName ? `${title} - ${this.RES.SITE_NAME}` : title;

    this.title.setTitle(_title);
    this.meta.updateTag({
      name: 'title',
      property: 'og:title',
      content: _title,
    });
    this.meta.updateTag({ property: 'twitter:title', content: _title });
  }
  protected setDescription(description: string) {
    this.meta.updateTag({
      name: 'description',
      property: 'og:description',
      content: description,
    });
  }

  protected setImage(imageUrl?: string) {
    // prepare image, either passed or
    const _imageUrl = imageUrl || this.config.SEO.defaultImage;

    this.meta.updateTag({
      name: 'image',
      property: 'og:image',
      content: _imageUrl,
    });
    this.meta.updateTag({ property: 'twitter:image', content: _imageUrl });
  }

  protected updateJsonSnippet(schema: any) {
    try {
      this._jsonSnippet ??= this.createJsonSnippet();

      // first find the graph objects then append to it
      const found = this._graphObjects.findIndex(
        (n) => n['@type'] === schema['@type'],
      );
      if (found > -1) {
        this._graphObjects[found] = schema;
      } else {
        this._graphObjects.push(schema);
      }

      const _graph = {
        '@context': 'https://schema.org',
        '@graph': this._graphObjects,
      };
      this.getJsonSnippet().textContent = JSON.stringify(_graph);
    } catch (err) {}
  }

  protected emptyJsonSnippet() {
    // sometimes, in browser platform, we need to empty objects first
    this._graphObjects = [];
  }

  /*** Public and Shared methods ***/
  public setPageMeta(data: {
    metaTitle: string;
    metaDesc: string;
    img?: string;
    canonicalUrl?: string;
    /** Defaults to true. See `setTitle`. */
    appendSiteName?: boolean;
  }) {
    // Clear graph objects to prevent memory accumulation across SSR requests
    this.emptyJsonSnippet();

    this.setTitle(
      data.metaTitle || this.RES.DEFAULT_PAGE_TITLE,
      data.appendSiteName ?? true,
    );
    this.setDescription(data.metaDesc || this.RES.DEFAULT_PAGE_DESC);
    this.setImage(data.img);
    // reset canonical
    this.setUrl(data.canonicalUrl);
  }

  /**
   * Emit the canonical link (and `og:url`) without touching title, description
   * or image. For pages that set those themselves — or not at all — and would
   * otherwise render with NO canonical tag, because nothing here ever ran.
   *
   * Pass the URL resolved from the route table (`${env.domain}/${staticPaths.x}`),
   * not a literal: one build serves every country by hostname. Omit it only when
   * the routed URL is genuinely the canonical one — it falls back to
   * `Router.url` against `env.domain`.
   */
  public setCanonicalUrl(url?: string) {
    this.setUrl(url);
  }

  /**
   * Manual noindex switch for a page. When `noindex` is true we emit
   * `<meta name="robots" content="noindex, follow">` so the page is dropped from
   * the organic index while still rendering normally for users and Ads traffic.
   * When false, we clear the tag (prod only, so the non-prod global noindex guard
   * in AppComponent is left intact). SSR renders fresh per request, so crawlers
   * always see the correct tag for the URL.
   */
  public setRobotsNoindex(noindex: boolean) {
    if (noindex) {
      this.meta.updateTag({ name: 'robots', content: 'noindex, follow' });
    } else if (this.env.production) {
      this.meta.removeTag("name='robots'");
    }
  }

  public calcWordCount(str: string) {
    return str.split(' ').filter((n) => n !== '').length;
  }

  private schemaBreadcrumbHome(): string {
    return this.translateService.data?.SEO?.SCHEMA?.BREADCRUMB_HOME ?? 'Home';
  }

  /****
   * SCHEMA IMPLEMENTATIONS
   * Encapsulate later for SCHEMA CONF FILE
   ****/
  /**
   * BreadcrumbList schema built from the same parts the visible breadcrumb
   * renders — the parts already carry the routed URLs (incl. the trailing
   * `hh`/`hh/r`/`hh/b` suffixes), so the schema can never drift into
   * non-routable @ids the way per-field reconstruction did.
   */
  public setVerticalSchema(partsOfUrl: { name: string; url: string }[]) {
    this.updateJsonSnippet(this.buildBreadCrumbSchema(partsOfUrl));
  }

  public setAIPriceGuideBreadCrumbSchema(
    partsOfUrl: { name: string; url: string }[],
  ) {
    return this.buildBreadCrumbSchema(partsOfUrl);
  }

  public setPriceGuideBreadCrumbSchema(
    partsOfUrl: { name: string; url: string }[],
  ) {
    return this.buildBreadCrumbSchema(partsOfUrl);
  }

  public buildBreadCrumbSchema(partsOfUrl: { name: string; url: string }[]) {
    // A crumb without a resolvable name or url would surface in Search
    // Console as "Unnamed item" — drop it rather than emit it.
    const parts = (partsOfUrl ?? []).filter(
      (part) => part?.name?.trim() && part?.url?.trim(),
    );
    return {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        // Google requires 1-based positions: "Position 1 signifies the
        // beginning of the trail"
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': toAbsoluteUrl(this.env.domain, '/'),
            name: this.schemaBreadcrumbHome(),
          },
        },
        ...parts.map((part, index) => ({
          '@type': 'ListItem',
          position: index + 2,
          item: {
            '@id': toAbsoluteUrl(this.env.domain, part.url),
            name: part.name,
          },
        })),
      ],
    };
  }

  /**
   * @param canonicalUrl Absolute canonical URL of the page. Required so the
   * Article's `url` / `mainEntityOfPage` point at the page that actually
   * renders this content. They were previously assembled as
   * `/{category}/{pris}/{slug}/{guide}`, a shape no route serves — every price
   * guide declared a 404 as its `mainEntityOfPage`.
   */
  public setPriceGuideArticleSchema(p: PriceGuide, canonicalUrl: string) {
    const pg = this.translateService.data?.SEO?.PRICE_GUIDE_ARTICLE;
    const editorSuffix = pg?.EDITOR_SUFFIX ?? ' artikel';
    const genre = pg?.GENRE ?? 'Prisoverslag og prisvejledninger';
    const keywordsTpl =
      pg?.KEYWORDS ??
      '{{category}} hjælp og pris guide vejledning 2024, online tilbud, få et overslag';
    const descTpl =
      pg?.DESCRIPTION ??
      'Hvor meget koster {{category}} estimat og guide til at ansætte nogen af dit hjem? Vores prisguide opdeler priserne for forskellige {{category}} ydelser.';
    const cat = p.category;
    const authorName =
      p.author ??
      this.translateService.data?.SEO?.SCHEMA?.DEFAULT_ARTICLE_AUTHOR ??
      this.env.appName;
    this.updateJsonSnippet({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: p.title,
      alternativeHeadline: `${p?.content?.FAQ_area?.[1]?.TITLE}` || p.title,
      image: p.imgUrl,
      award: 'Organization',
      editor: `${this.env.appName}${editorSuffix}`,
      genre,
      keywords: keywordsTpl.replace(/\{\{category\}\}/g, cat),
      wordcount: this.calcWordCount(p.content.rich_text),
      mainEntityOfPage: canonicalUrl,
      publisher: {
        '@type': 'Organization',
        name: this.env.appName,
        logo: {
          '@type': 'ImageObject',
          url: `${this.env.cloudFrontImgUrl}/private/compressed/192x192_logo.webp`,
        },
      },
      url: canonicalUrl,
      datePublished: p.date,
      dateCreated: p.date,
      dateModified: p.date,
      description: descTpl.replace(/\{\{category\}\}/g, cat),
      articleBody: p.content.rich_text,
      author: {
        '@type': 'Person',
        name: authorName,
      },
    });
  }

  public setArticleScheme(articleSchema: any) {
    this.updateJsonSnippet(articleSchema);
  }

  /**
   * Inject an arbitrary JSON-LD object into the page's `@graph` (deduped by
   * `@type`). Use this instead of a `<script type="application/ld+json">` in a
   * component template — Angular strips `<script>` from templates, so
   * template-based JSON-LD never reaches the SSR output.
   */
  public setJsonLdSchema(schema: any) {
    this.updateJsonSnippet(schema);
  }

  public setServiceSchema(serviceData: {
    name: string;
    providerName: string;
    providerUrl: string;
    serviceType: string;
    offers: Array<{
      name: string;
      price: string;
      priceCurrency: string;
    }>;
  }) {
    this.updateJsonSnippet({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: serviceData.name,
      provider: {
        '@type': 'LocalBusiness',
        name: serviceData.providerName,
        url: serviceData.providerUrl,
      },
      serviceType: serviceData.serviceType,
      offers: serviceData.offers.map((offer) => ({
        '@type': 'Offer',
        name: offer.name,
        price: offer.price,
        priceCurrency: offer.priceCurrency,
      })),
    });
  }

  public setEnhancedServiceSchema(
    v: Vertical,
    URL: string,
    reviewData?: { averageRating: number; reviewCount: number },
  ) {
    const serviceName = v.cat || v.sPM || 'Service';
    const locationText = this.getLocationText(v);
    const serviceUrl = `${this.env.domain}${URL}`;

    const schema: any = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: `${serviceName}${locationText}`,
      url: serviceUrl,
      ...(v.imgUrl && { image: v.imgUrl }),
      provider: {
        '@type': 'LocalBusiness',
        url: this.env.domain,
        logo: `${this.env.cloudFrontImgUrl}/private/compressed/lp/webp/hh_logo_blue.webp`,
        name: this.env.appName,
        image: `${this.env.cloudFrontImgUrl}/private/compressed/lp/webp/vertical+hero+images/man-building-a-box-blue-min.webp`,
        sameAs: [
          this.env.social.fb,
          this.env.social.instagram,
          this.env.social.linkedin,
          'https://www.youtube.com/channel/UCr-ZzmZrbXW3oJrUhr_qvUQ',
          'https://dk.pinterest.com/handyhanddk/',
        ],
      },
    };

    // Add aggregate rating if review data is provided
    if (reviewData && reviewData.reviewCount > 0) {
      schema.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: reviewData.averageRating.toFixed(1),
        reviewCount: reviewData.reviewCount,
        bestRating: '5',
        worstRating: '1',
      };
    }

    this.updateJsonSnippet(schema);
  }

  private getLocationText(v: Vertical): string {
    const schema = this.translateService.data?.SEO?.SCHEMA;
    const cityTpl = schema?.SERVICE_SUFFIX_IN_CITY ?? ' in {{place}}';
    const regionTpl = schema?.SERVICE_SUFFIX_IN_REGION ?? ' in {{place}}';
    if (v.city && v.region) {
      return cityTpl.replace(/\{\{place\}\}/g, v.city.city);
    }
    if (v.region) {
      return regionTpl.replace(/\{\{place\}\}/g, v.region.name);
    }
    return '';
  }

  public setAppScheme() {
    this.updateJsonSnippet({
      '@context': 'https://schema.org',
      '@type': 'MobileApplication',
      name: this.env.appName,
      operatingSystem: ['Android', 'iOS'],
      applicationCategory: 'LifestyleApplication',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.6',
        ratingCount: '1300',
      },
    });
  }

  // DOCS = https://developers.google.com/search/docs/appearance/structured-data/sitelinks-searchbox
  public setSitelinksSearchboxSchema(url: string) {
    return this.updateJsonSnippet({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${this.env.domain}/search?query={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    });
  }
}
